import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EventAPI } from '../../../network';
import { EventModel } from '../../../network/rest/models/EventModel';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import Event from '../../components/Event';
import Pagination from '../../components/Pagination';
import './style.scss';

function Events() {
    const [data, setData] = useState<EventModel[]>([]);
    const [searchParams] = useSearchParams();
    const [dataCount, setDataCount] = useState(0);
    const [controller, setController] = useState({
        page: 0,
        rowsPerPage: 10,
    });

    const dateQuery = searchParams.get('date');
    const searchQuery = searchParams.get('search');
    const previousQuery = useRef({ date: dateQuery, search: searchQuery });

    async function getData() {
        try {
            const response = await EventAPI.getEventsApiV1EventsGet({
                offset: controller.page * controller.rowsPerPage,
                limit: controller.rowsPerPage,
                search: searchQuery || '',
            });
            if (response) {
                setData(response.events);
                setDataCount(response.total);
            }
        } catch (e) {
            ApiErrorHandler(e, true);
        }
    }

    async function getDataByDate() {
        if (!dateQuery) {
            return;
        }

        const formatedDate = new Date(new Date(+dateQuery).setHours(12));

        try {
            const response = await EventAPI.getEventsByDateApiV1EventsEventDateGet({
                date: formatedDate,
            });
            if (response) {
                setData(response.events);
            }
        } catch (e) {
            ApiErrorHandler(e, true);
        }
    }

    useEffect(() => {
        const isNewQuery =
            previousQuery.current.date !== dateQuery ||
            previousQuery.current.search !== searchQuery;
        previousQuery.current = { date: dateQuery, search: searchQuery };

        // a new query invalidates the current offset, so go back to the first
        // page and let that controller change trigger the fetch
        if (isNewQuery && controller.page !== 0) {
            handlePageChange(0);
            return;
        }

        if (dateQuery) {
            getDataByDate();
        } else {
            getData();
        }
    }, [controller, searchQuery, dateQuery]);

    function handlePageChange(newPage: number) {
        setController({
            ...controller,
            page: newPage,
        });
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setController({
            ...controller,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0,
        });
    };

    return (
        <div className="Events">
            <h1>{translations.events}</h1>
            {data.map((event, idx) => {
                return (
                    <div className="show-wrapper" key={`${event.id}${idx}`}>
                        {idx !== 0 && event.date?.getMonth() !== data[idx - 1].date.getMonth() ? (
                            <div className="month">
                                {event.date
                                    .toLocaleDateString('sl', {
                                        month: 'long',
                                    })
                                    .toUpperCase()}
                            </div>
                        ) : null}
                        {idx === 0 ||
                        Math.abs(event.date?.getTime() - data[idx - 1].date.getTime()) >
                            86400000 ? (
                            <div className="date">
                                {event.date?.toLocaleDateString('sl', {
                                    weekday: 'long',
                                    month: '2-digit',
                                    day: '2-digit',
                                })}
                            </div>
                        ) : null}
                        <Event data={event} />
                    </div>
                );
            })}
            {/* the by-date endpoint returns every match at once, so there is
                nothing to page through while a date is selected */}
            {dateQuery ? null : (
                <Pagination
                    page={controller.page}
                    count={dataCount}
                    rowsPerPage={controller.rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            )}
        </div>
    );
}

export default Events;
